import { Classes, Spinner, TextArea } from "@blueprintjs/core"
import classNames from "classnames"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import React, { useCallback, useRef, useState } from "react"
import {
  FEEDBACK_MAX_ATTACHMENTS,
  FEEDBACK_MAX_FILE_SIZE,
  FeedbackAttachment,
  FeedbackStoreType,
} from "../models/feedback-store"
import styles from "./Feedback.module.scss"
import { formatFileSize } from "./format"

interface DraftFile {
  key: string
  name: string
  size: number
  attachment: FeedbackAttachment | null
}

let draftKey = 0

/** Поле ввода: текст, скрепка, перетаскивание и вставка файлов из буфера */
export const FeedbackComposer: React.FC<FeedbackComposerProps> = ({ store, onSend, disabled }) => {
  const [text, setText] = useState("")
  const [files, setFiles] = useState<DraftFile[]>([])
  const [dragging, setDragging] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const uploading = files.some((f) => !f.attachment)
  const ready = files.filter((f) => !!f.attachment).map((f) => f.attachment as FeedbackAttachment)
  const canSend = !disabled && !uploading && (!!text.trim() || ready.length > 0)

  const addFiles = useCallback(
    (list: FileList | File[] | null) => {
      const selected = Array.from(list || [])

      if (!selected.length) {
        return
      }

      const free = FEEDBACK_MAX_ATTACHMENTS - files.length

      if (selected.length > free) {
        store.notify.warning(`Можно прикрепить не больше ${FEEDBACK_MAX_ATTACHMENTS} файлов`)
      }

      selected.slice(0, Math.max(free, 0)).forEach((file) => {
        if (file.size > FEEDBACK_MAX_FILE_SIZE) {
          store.notify.warning(`Файл «${file.name}» больше ${formatFileSize(FEEDBACK_MAX_FILE_SIZE)}`)
          return
        }

        const key = `${++draftKey}`

        setFiles((current) => [...current, { key, name: file.name, size: file.size, attachment: null }])

        store.uploadAttachment(file).then((attachment) =>
          setFiles((current) =>
            attachment
              ? current.map((f) => (f.key === key ? { ...f, attachment } : f))
              : current.filter((f) => f.key !== key)
          )
        )
      })
    },
    [files.length, store]
  )

  const send = useCallback(() => {
    if (!canSend) {
      return
    }

    onSend(text, ready)
    setText("")
    setFiles([])
  }, [canSend, onSend, text, ready])

  return (
    <div
      className={classNames(styles.composer, { [styles.composerDragging]: dragging > 0 })}
      onDragEnter={(e) => {
        e.preventDefault()
        setDragging((d) => d + 1)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setDragging((d) => Math.max(d - 1, 0))
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(0)
        !disabled && addFiles(e.dataTransfer.files)
      }}
    >
      {dragging > 0 && <div className={styles.dropHint}>Отпустите, чтобы прикрепить файлы</div>}

      {files.length > 0 && (
        <div className={styles.draftFiles}>
          {files.map((file) => (
            <span key={file.key} className={styles.draftFile}>
              {file.attachment ? <GeneralIcon type="general-doc" size={12} /> : <Spinner size={12} />}
              <span className={styles.fileName}>{file.name}</span>
              <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
              {file.attachment && (
                <GeneralIcon
                  type="general-cross-small"
                  size={12}
                  className={styles.draftRemove}
                  onClick={() => setFiles((current) => current.filter((f) => f.key !== file.key))}
                />
              )}
            </span>
          ))}
        </div>
      )}

      <div className={styles.composerRow}>
        <div className={`${Classes.INPUT_GROUP} planr-default-input ${styles.composerInput}`}>
          <TextArea
            value={text}
            disabled={disabled}
            className={Classes.FILL}
            growVertically={true}
            placeholder="Напишите сообщение…"
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => {
              if (e.clipboardData.files.length > 0) {
                e.preventDefault()
                addFiles(e.clipboardData.files)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault()
                send()
              }
            }}
          />
        </div>

        <PlanrButton
          type="secondary"
          size="small"
          icon="general-attach"
          title="Прикрепить файлы"
          disabled={disabled || files.length >= FEEDBACK_MAX_ATTACHMENTS}
          onClick={() => inputRef.current && inputRef.current.click()}
        />

        <PlanrButton type="primary" size="small" disabled={!canSend} onClick={send}>
          Отправить
        </PlanrButton>

        <input
          ref={inputRef}
          type="file"
          multiple={true}
          className={styles.fileInput}
          onChange={(e) => {
            addFiles(e.target.files)
            e.target.value = ""
          }}
        />
      </div>

      <div className={styles.hint}>Ctrl + Enter — отправить. Файлы можно перетащить или вставить из буфера</div>
    </div>
  )
}

interface FeedbackComposerProps {
  store: FeedbackStoreType
  onSend: (text: string, attachments: FeedbackAttachment[]) => void
  disabled: boolean
}
